import React, { useContext, useState } from 'react';
import { getDay, previousMonday, startOfDay, format  } from "date-fns";
import {makeStyles, tokens, shorthands } from "@fluentui/react-components";
import { useMediaQuery  } from 'react-responsive'

const GlobalStatusContext = React.createContext();

export function useGlobalStatus() {
    return useContext(GlobalStatusContext);
}

//also use makestyles to enable token based colours
const useStyles = makeStyles({
    empty: { 
    },
    open: { 
    },
    closed: {      
        backgroundColor: '#c2c2c2'
    },
    rejected: { 
        backgroundColor: '#f4bfb8',
        borderRadius: '12px'
    },
    approved: { 
        ...shorthands.borderColor(tokens.colorPaletteGreenForeground2),
        backgroundColor: '#8cbeb3'
    },
    error: { 
        ...shorthands.borderColor(tokens.colorPaletteRedForeground2),
        backgroundColor: tokens.colorPaletteRedBackground1
    },
    outOfProjectRange: {
        backgroundColor: tokens.colorPaletteMinkBackground2
    },
    outOfUserRange: {
        backgroundColor: tokens.colorPaletteMinkBackground2
    },
    outOfTimecodeRange: {
        backgroundColor: tokens.colorPaletteMinkBackground2
    },
    accordionHeader: {
        backgroundColor: tokens.colorNeutralBackground4,
        padding: '3px',
        borderRadius: '8px',
        margin: '4px 0',
    },
    accordionPanel: {
        backgroundColor: tokens.colorNeutralBackground3,
        padding: '3px',
        borderRadius: '8px',
        margin: '4px 0',
    },
    mobileDayTotals: {
        backgroundColor: tokens.colorNeutralBackground3,
        padding: "7px 9px",
        borderRadius: "10px",
        display: "flex",
        justifyContent: "space-between",
    },
    buttonPrimary: {
        backgroundColor: "#549687", 
        color: tokens.colorNeutralForegroundOnBrand, 
        border: "1px solid transparent",
        borderRadius: tokens.borderRadiusMedium, 
        padding: "8px 16px",
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold, 
        lineHeight: "20px",
        cursor: "pointer",
        boxShadow: tokens.shadow4,
        transition: "background-color 0.2s ease, border-color 0.2s ease",
    
        ":hover": {
          backgroundColor: "#467d71",
          color: tokens.colorNeutralForegroundOnBrand, 
          boxShadow: tokens.shadow8,
        },
    
        ":active": {
          backgroundColor: "#3a6b60",
          boxShadow: tokens.shadow2,
        },
    
        ":focus-visible": {
          outline: "2px solid",
          outlineColor: tokens.colorStrokeFocus2,
          outlineOffset: "2px",
        },
        
        ":disabled": {
          backgroundColor: tokens.colorNeutralBackgroundDisabled,
          color: tokens.colorNeutralForegroundDisabled,
          cursor: "not-allowed",
          boxShadow: 'none',
        },
        ":disabled:hover": {
            backgroundColor: tokens.colorNeutralBackgroundDisabled,
            color: tokens.colorNeutralForegroundDisabled,
            boxShadow: 'none',
        },
    },
    buttonSecondary: {
        backgroundColor: "#2C353D",
        color: "#ffffff",
        border: "1px solid transparent",
        borderRadius: tokens.borderRadiusMedium,
        padding: "8px 16px",
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        cursor: "pointer",
        boxShadow: tokens.shadow4,
        transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
    
        ":hover": {
          backgroundColor: "#868589",
          boxShadow: tokens.shadow8,
        },
    
        ":active": {
          backgroundColor: "#767577",
          boxShadow: tokens.shadow2,
        },
    
        ":focus-visible": {
          outline: "2px solid",
          outlineColor: tokens.colorStrokeFocus2,
          outlineOffset: "2px",
        },
    
        ":disabled": {
          backgroundColor: "#cccccc",
          color: "#ffffff",
          cursor: "not-allowed",
          boxShadow: "none",
        },
    
        ":disabled:hover": {
          backgroundColor: "#cccccc",
          boxShadow: "none",
        },
    },
    buttonStrong: {
        backgroundColor: "#DF4935", 
        color: tokens.colorNeutralForegroundOnBrand, 
        border: "1px solid transparent",
        borderRadius: tokens.borderRadiusMedium, 
        padding: "8px 16px",
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold, 
        lineHeight: "20px",
        cursor: "pointer",
        boxShadow: tokens.shadow4,
        transition: "background-color 0.2s ease, border-color 0.2s ease",
    
        ":hover": {
          backgroundColor: "#b62f1d",
          color: tokens.colorNeutralForegroundOnBrand, 
          boxShadow: tokens.shadow8,
        },
    
        ":active": {
          backgroundColor: "#972718",
          boxShadow: tokens.shadow2,
        },
    
        ":focus-visible": {
          outline: "2px solid",
          outlineColor: tokens.colorStrokeFocus2,
          outlineOffset: "2px",
        },
        
        ":disabled": {
          backgroundColor: tokens.colorNeutralBackgroundDisabled,
          color: tokens.colorNeutralForegroundDisabled,
          cursor: "not-allowed",
          boxShadow: 'none',
        },
        ":disabled:hover": {
            backgroundColor: tokens.colorNeutralBackgroundDisabled,
            color: tokens.colorNeutralForegroundDisabled,
            boxShadow: 'none',
        },
    },
    iconOnlyButton: {
        composes: 'buttonPrimary',
        width: '40px',
        height: '40px',
        padding: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
        tooltip: {
        backgroundColor: '#D38200',
        color: 'white'
    },
    icon18: { fontSize: "18px" },
    icon24: { fontSize: "24px" },
    icon32: { fontSize: "32px" },
    icon48: { fontSize: "48px" }
  });
  

export function GlobalStatusProvider({ loading, setLoading, isAddTimecodeDisabled, setIsAddTimecodeDisabled, isTimesheetSubmitted, setIsTimesheetSubmitted, timesheetStatus, setTimesheetStatus, isCasualSubmitLocked, setIsCasualSubmitLocked, lockedMonths, setLockedMonths, children }) {

    /****************************************************************************************************************************
    LOADER        
    *****************************************************************************************************************************/
    
        //express loading status as a bool for use by compoents
        const startLoad = () => {
            setLoading(true);
        };
        
        const endLoad = () => {   
             setLoading(false);   
        };
    
    
    /****************************************************************************************************************************
    NOTIFICATION BADGE        
    *****************************************************************************************************************************/
    
        //express notification status as a integer for use by compoents
        const [notifications, setnotifications] = useState(0);

        const incrementNotificationCount = (amount) => {
            setnotifications(notifications + amount);
        };
    

    /****************************************************************************************************************************
    SUBMIT        
    *****************************************************************************************************************************/
    
        const markTimesheetSubmitted = () => {
            setIsTimesheetSubmitted(true);
        };
          
        const markTimesheetUnsubmitted = () => {
            setIsTimesheetSubmitted(false); // Need to change this to false when we want users to be able to submit!
        };
        
        // To disable the add timecode button
        const disableAddTimecode = () => {
            setIsAddTimecodeDisabled(true);
        };
        
        const enableAddTimecode = () => {
            setIsAddTimecodeDisabled(false);
        };
          
    
    /****************************************************************************************************************************
    CURRENT WEEK
    This sets the current week used through the system. Decrement and increment should only happen from one place but if needed, 
    the provider could express those as well        
    *****************************************************************************************************************************/

        //calculate the start of the week on initial load to be current week.
        var today = Date();
        const [currentWeekStart, setCurrentWeekStart]= useState(
            getDay(today) > 1 ? previousMonday(startOfDay(today)) : startOfDay(today)
        );

        const [lastWeekStart, setLastWeekStart]= useState(
            previousMonday(currentWeekStart)
        );

        const currentWeekStartDisplay = () => {
            var weekEnd = new Date(currentWeekStart);
            weekEnd.setDate(currentWeekStart.getDate() + 6);
            return format(currentWeekStart,'d MMMM')+" - "+format(weekEnd,'d MMMM');
            // return format(currentWeekStart,'PPP');
        };

    /****************************************************************************************************************************
    CURRENT MEDIA SIZE
    This checks the media width to see if the app should display content for a desktop device or a mobile device.    
    *****************************************************************************************************************************/

        const isDesktop = useMediaQuery({
            query: '(min-width: 920px)'
          })

    // Timesheet status for day view (false means not editable and true means editable)
    // const [timesheetStatus, setTimesheetStatus] = useState(false);
            const [dateToggle, setDateToggle]= useState(false);
            
 
          
    const value = {
        loading,
        startLoad,
        endLoad,
        isTimesheetSubmitted,
        setIsTimesheetSubmitted,
        markTimesheetSubmitted,
        markTimesheetUnsubmitted,
        isAddTimecodeDisabled,
        disableAddTimecode,
        enableAddTimecode,
        notifications,
        incrementNotificationCount,
        currentWeekStart,
        setCurrentWeekStart, 
        lastWeekStart,
        setLastWeekStart,  
        currentWeekStartDisplay,  
        useStyles,
        isDesktop,
        timesheetStatus,
        setTimesheetStatus,
        isCasualSubmitLocked, 
        setIsCasualSubmitLocked,
        lockedMonths, 
        setLockedMonths,
        dateToggle,
        setDateToggle
      };
    
    return <GlobalStatusContext.Provider value={value}>{children}</GlobalStatusContext.Provider>;
}